import * as React from "react";
import { ChevronDown, ChevronRight, Play, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type { DDQParsedQuestions } from "@/lib/api";

const ESTIMATED_SEC_PER_QUESTION = 0.7; // ~65s for 100 questions w/ 8 concurrent

function formatDuration(seconds: number): string {
  const total = Math.max(15, Math.round(seconds));
  if (total < 60) return `~${total}s`;
  const m = Math.floor(total / 60);
  const s = total % 60;
  return s > 0 ? `~${m}m ${s}s` : `~${m}m`;
}

export function DDQReviewDialog({
  open,
  displayName,
  parsed,
  fundName,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  displayName: string;
  parsed: DDQParsedQuestions | null;
  fundName: string;
  onCancel: () => void;
  onConfirm: (selectedQuestionIds: string[]) => void;
}) {
  // Selection state — default to "all selected" whenever a new parse arrives.
  const [selected, setSelected] = React.useState<Set<string>>(new Set());
  const [expanded, setExpanded] = React.useState<Set<string>>(new Set());

  React.useEffect(() => {
    if (!parsed) {
      setSelected(new Set());
      setExpanded(new Set());
      return;
    }
    const all = new Set<string>();
    const allSections = new Set<string>();
    for (const s of parsed.sections) {
      allSections.add(s.name);
      for (const q of s.questions) all.add(q.id);
    }
    setSelected(all);
    setExpanded(allSections);
  }, [parsed]);

  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onCancel(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onCancel]);

  if (!open || !parsed) return null;

  const totalQuestions = parsed.sections.reduce((acc, s) => acc + s.questions.length, 0);
  const selectedCount = selected.size;
  const sectionMap = new Map(parsed.sections.map((s) => [s.name, s.questions.map((q) => q.id)]));

  const toggleQuestion = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSection = (sectionName: string) => {
    const ids = sectionMap.get(sectionName) || [];
    const allSelected = ids.every((id) => selected.has(id));
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) for (const id of ids) next.delete(id);
      else for (const id of ids) next.add(id);
      return next;
    });
  };
  const toggleExpanded = (sectionName: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(sectionName)) next.delete(sectionName); else next.add(sectionName);
      return next;
    });
  };

  const confirm = () => {
    if (selectedCount === 0) return;
    onConfirm(Array.from(selected));
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4">
      <div className="flex max-h-[90svh] w-full max-w-2xl flex-col rounded-md border bg-card shadow-lg">
        <div className="flex items-start justify-between gap-3 border-b p-5">
          <div className="min-w-0">
            <h3 className="text-base font-semibold">Review parsed questions</h3>
            <p className="mt-1 text-xs text-muted-foreground">
              <span className="font-medium text-foreground">{displayName}</span>
              {" — "}
              {totalQuestions} questions across {parsed.sections.length} sections.
              {parsed.truncated ? " (Capped at 200 questions.)" : ""} Uncheck any
              you don&rsquo;t want answered, then start the run.
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
              <Badge variant="outline">Fund: {fundName}</Badge>
              <Badge variant="outline">
                {selectedCount} of {totalQuestions} selected
              </Badge>
              <Badge variant="outline">
                Est. time: {formatDuration(selectedCount * ESTIMATED_SEC_PER_QUESTION)}
              </Badge>
            </div>
          </div>
          <Button type="button" variant="ghost" size="icon" aria-label="Close" onClick={onCancel}>
            <X />
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          <ul className="space-y-3">
            {parsed.sections.map((section) => {
              const ids = section.questions.map((q) => q.id);
              const sectionSelectedCount = ids.filter((id) => selected.has(id)).length;
              const allSelected = sectionSelectedCount === ids.length;
              const noneSelected = sectionSelectedCount === 0;
              const isExpanded = expanded.has(section.name);
              return (
                <li key={section.name} className="rounded-md border">
                  <div className="flex items-center justify-between gap-3 px-3 py-2">
                    <label className="flex min-w-0 flex-1 items-center gap-2 text-sm font-medium">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        ref={(el) => {
                          if (el) el.indeterminate = !allSelected && !noneSelected;
                        }}
                        onChange={() => toggleSection(section.name)}
                      />
                      <span className="min-w-0 truncate">{section.name}</span>
                      <span className="ml-1 text-xs text-muted-foreground">
                        ({sectionSelectedCount}/{ids.length})
                      </span>
                    </label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      aria-label={isExpanded ? "Collapse section" : "Expand section"}
                      onClick={() => toggleExpanded(section.name)}
                    >
                      {isExpanded ? <ChevronDown /> : <ChevronRight />}
                    </Button>
                  </div>
                  {isExpanded ? (
                    <ul className="border-t bg-background px-3 py-2">
                      {section.questions.map((q) => (
                        <li key={q.id} className="py-1.5">
                          <label className="flex items-start gap-2 text-sm">
                            <input
                              type="checkbox"
                              checked={selected.has(q.id)}
                              onChange={() => toggleQuestion(q.id)}
                              className="mt-1"
                            />
                            <span className="min-w-0">
                              <span className="font-mono text-xs text-muted-foreground">
                                {q.id}.
                              </span>{" "}
                              {q.text}
                              {q.sub_questions && q.sub_questions.length > 0 ? (
                                <ol className="mt-1 list-[lower-alpha] pl-5 text-xs text-muted-foreground">
                                  {q.sub_questions.map((sq, i) => (
                                    <li key={i}>{sq}</li>
                                  ))}
                                </ol>
                              ) : null}
                            </span>
                          </label>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 border-t p-4">
          <div className="text-xs text-muted-foreground">
            Answers honor the same redaction rules as live chat — per-recipient
            compensation, individual grant unit counts, and litigation dollar
            exposure are not included.
          </div>
          <div className="flex items-center gap-2">
            <Button type="button" variant="ghost" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="button" onClick={confirm} disabled={selectedCount === 0}>
              <Play />
              Answer {selectedCount === totalQuestions ? "all" : selectedCount} question{selectedCount === 1 ? "" : "s"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
